/**
 * AI メニュー開発 (v0.3): propose_menu ツールの構造化提案を描画するカード。
 * 詳細: docs/design/v03-ai-menu-dev-improvements.md §6.3
 *
 * XSS対策 (Security M-1): menu_name/instruction/notes等は全てReactの自動エスケープで平文表示する
 * (dangerouslySetInnerHTML は使わない)。
 */
import { useMemo, useState } from 'react';
import { Star, RotateCw, ChefHat } from 'lucide-react';
import { roundQuantityByUnit } from '@/lib/round-quantity-by-unit';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';

export type ProposalIngredient = {
  group: string;
  name: string;
  quantity: number;
  unit: string;
  unit_cost_yen?: number;
  notes?: string;
};
export type ProposalRecipeStep = { step_no: number; instruction: string };
export type ProposalRecipeSection = { section: string; steps: ProposalRecipeStep[] };
export type StructuredProposal = {
  menu_name: string;
  category_hint?: string;
  target_customer_summary?: string;
  base_servings: number;
  ingredients: ProposalIngredient[];
  recipe_sections: ProposalRecipeSection[];
  cost_estimate_yen: number;
  cost_ratio_percent: number;
  suggested_price_yen: number;
  cost_notes?: string;
  profile_affinity?: string;
};

const groupByOrder = <T,>(items: T[], keyOf: (item: T) => string, order: string[] | undefined): Array<[string, T[]]> => {
  const byKey = new Map<string, T[]>();
  for (const item of items) {
    const key = keyOf(item);
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key)!.push(item);
  }
  const known = order?.filter((k) => byKey.has(k)) ?? [];
  const unknown = [...byKey.keys()].filter((k) => !known.includes(k));
  return [...known, ...unknown].map((k) => [k, byKey.get(k)!]);
};

export function StructuredProposalCard({
  proposal, industryGroups, industrySections, isGenerating, onFavorite, onRegenerate, regenerating, readOnly,
  canFavorite = true,
}: {
  proposal: StructuredProposal;
  industryGroups?: string[];
  industrySections?: string[];
  isGenerating?: boolean;
  onFavorite?: () => void;
  onRegenerate?: (servings: number) => void;
  regenerating?: boolean;
  /** お気に入り一覧の詳細表示など、保存済み提案を閲覧専用で描画する場合はtrue (お気に入り/再生成ボタンを非表示) */
  readOnly?: boolean;
  /** ⚠️ ai_menu_favorites:manage権限が無いユーザーには「お気に入り」ボタンを表示しない(§8-184)。
   *  POST /menu-favoritesはai_menu_chatとは別feature(ai_menu_favorites)のmanageを要求するため、
   *  チャット自体は使えてもお気に入り保存はできない権限構成があり得る。既定trueは既存呼び出し元
   *  (お気に入り詳細のreadOnly表示等、そもそもこのボタンを出さない)との後方互換のため。 */
  canFavorite?: boolean;
}) {
  const baseServings = proposal.base_servings || 1;
  const [displayServings, setDisplayServings] = useState(baseServings);
  const [regenerateInput, setRegenerateInput] = useState(String(baseServings));

  // readOnly (お気に入り詳細等、DB永続化データを表示するケース) では生成直後の信頼できる出力とは
  // 前提が異なるため、配列であることを念のため防御する (design-and-codeレビュー反映、HIGH)
  const groupedIngredients = useMemo(
    () => groupByOrder(proposal.ingredients ?? [], (i) => i.group, industryGroups),
    [proposal.ingredients, industryGroups],
  );
  const groupedSections = useMemo(
    () => groupByOrder(proposal.recipe_sections ?? [], (s) => s.section, industrySections)
      .flatMap(([, sections]) => sections),
    [proposal.recipe_sections, industrySections],
  );

  const scale = displayServings / baseServings;
  const scaledCost = Math.round(proposal.cost_estimate_yen * scale);
  const scaledRevenue = Math.round(proposal.suggested_price_yen * displayServings);
  const scaledRatio = scaledRevenue > 0 ? (scaledCost / scaledRevenue) * 100 : proposal.cost_ratio_percent;

  return (
    <div
      className="rounded-xl border border-surface-border bg-surface-card shadow-sm overflow-hidden"
      aria-busy={isGenerating ? 'true' : 'false'}
    >
      <div className="px-4 py-3 border-b border-surface-line">
        <div className="flex items-start gap-2">
          <ChefHat size={18} className="text-brand-red shrink-0 mt-0.5" />
          <div className="min-w-0">
            <h3 className="font-semibold text-text-main truncate">{proposal.menu_name}</h3>
            <p className="text-xs text-text-sub mt-0.5">
              {proposal.target_customer_summary && <>対象: {proposal.target_customer_summary} / </>}
              価格 {proposal.suggested_price_yen}円 / 原価率 {proposal.cost_ratio_percent.toFixed(1)}%
            </p>
          </div>
        </div>
      </div>

      <div className="px-4 py-3 border-b border-surface-line flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-text-body">材料 ({baseServings}人前基準)</span>
        <label className="flex items-center gap-1.5 text-xs text-text-sub">
          表示人数:
          <Select value={String(displayServings)} onValueChange={(v) => setDisplayServings(Number(v))}>
            <SelectTrigger aria-label="材料・原価の表示人数" className="min-h-[44px] sm:min-h-0 w-auto min-w-[5.5rem]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[...new Set([1, baseServings, baseServings * 2, 10, 20, 50, 100].filter((n) => n > 0))]
                .sort((a, b) => a - b)
                .map((n) => <SelectItem key={n} value={String(n)}>{n}人前</SelectItem>)}
            </SelectContent>
          </Select>
        </label>
      </div>

      <div className="px-4 py-3 border-b border-surface-line overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">{displayServings}人前の材料</caption>
          {groupedIngredients.map(([group, items]) => (
            <tbody key={group}>
              <tr>
                <th scope="rowgroup" colSpan={3} className="text-left text-xs font-semibold text-text-sub pt-2 pb-1 border-b border-dashed border-surface-line">
                  {group}
                </th>
              </tr>
              {items.map((ing, idx) => {
                const scaled = roundQuantityByUnit(ing.quantity * scale, ing.unit);
                return (
                  <tr key={`${group}-${idx}`} className="text-text-body">
                    <td className="py-1 pr-3">{ing.name}</td>
                    <td className="py-1 pr-3 text-right tabular-nums whitespace-nowrap">{scaled.display}</td>
                    <td className="py-1 text-right text-text-sub whitespace-nowrap">
                      {ing.unit_cost_yen != null ? `@ ${ing.unit_cost_yen.toLocaleString()}円/${ing.unit}` : ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          ))}
        </table>
      </div>

      <div className="px-4 py-3 border-b border-surface-line">
        <span className="text-sm font-medium text-text-body">作り方</span>
        {groupedSections.map((section, sIdx) => (
          <div key={`${section.section}-${sIdx}`} className="mt-2">
            <div className="text-xs font-semibold text-text-sub">{section.section}</div>
            <ol className="mt-1 space-y-1 text-sm text-text-body list-decimal list-inside">
              {section.steps.map((step) => (
                <li key={step.step_no}>{step.instruction}</li>
              ))}
            </ol>
          </div>
        ))}
      </div>

      <div className={`px-4 py-3 text-sm text-text-body space-y-0.5 ${readOnly ? '' : 'border-b border-surface-line'}`}>
        <div>原価内訳: 材料 {scaledCost.toLocaleString()}円</div>
        <div>販売価格 {proposal.suggested_price_yen.toLocaleString()}円 × {displayServings} = {scaledRevenue.toLocaleString()}円</div>
        <div>原価率 {scaledRatio.toFixed(1)}%</div>
        {proposal.cost_notes && <div className="text-xs text-text-sub mt-1">{proposal.cost_notes}</div>}
      </div>

      {!readOnly && (
        <div className="px-4 py-3 flex flex-wrap items-center gap-3">
          {canFavorite && (
            <button
              type="button"
              onClick={onFavorite}
              disabled={isGenerating}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-brand-red text-white text-xs font-medium hover:bg-brand-red-hover disabled:opacity-50"
            >
              <Star size={14} /> {isGenerating ? '生成完了までお待ちください' : 'お気に入り'}
            </button>
          )}
          <div className="flex flex-col items-end gap-1 ml-auto">
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                min={1}
                max={500}
                value={regenerateInput}
                onChange={(e) => setRegenerateInput(e.target.value)}
                disabled={isGenerating || regenerating}
                className="w-16 border border-surface-border rounded px-2 py-1 text-xs disabled:opacity-50"
                aria-label="再生成する人数"
              />
              <button
                type="button"
                onClick={() => { const n = Number(regenerateInput); if (n >= 1 && n <= 500) onRegenerate?.(n); }}
                disabled={isGenerating || regenerating}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-surface-border text-text-body text-xs hover:bg-surface-sunken disabled:opacity-50"
              >
                <RotateCw size={13} className={regenerating ? 'animate-spin' : ''} /> 別の人数で再生成
              </button>
            </div>
            <span className="text-[11px] text-text-muted">工程まで書き直し・約30秒・token消費あり</span>
          </div>
        </div>
      )}
    </div>
  );
}
